import type { TokenSymbol } from '$lib/types/tokens';
import { getDecimalOfToken, toBN } from '$lib/util/util';
import BigNumber from 'bignumber.js';
import type { ethers } from 'ethers';
import { derived, writable, type Readable, type Writable } from 'svelte/store';
import { nativeUserBalances, pythPrices, userBalances, userBalancesEnriched } from '../store';
import type { AddFundsInsufficientBalanceStatus } from '$lib/home/scwallet/types';
import { MIN_ETH_BALANCE } from '$lib/web3/contants';
import { tokensAllowedForDeposit } from './config';

export const checkingSmartContractWallet: Writable<boolean> = writable(true);
export const smartContractWalletAddress: Writable<string> = writable('');
export const overrideSmartContractWalletAddress: Writable<string> = writable('');

export const smartContractSideMenuOpen: Writable<boolean> = writable(false);
export const depositorWithdraw: Writable<'Deposit' | 'Withdraw'> = writable('Deposit');

export const smartWalletName: Writable<string> = writable('Smart Wallet');

export const processingFundDeposit: Writable<boolean> = writable(false);
export const addFundsToken: Writable<TokenSymbol> = writable('sUSD');
export const addFundsTokenAmount: Writable<ethers.BigNumber> = writable(toBN('0'));
export const addFundsAmount: Readable<ethers.BigNumber> = derived(
	[addFundsTokenAmount, addFundsToken, pythPrices],
	([$addFundsTokenAmount, $addFundsToken, $pythPrices]) => {
		if ($addFundsTokenAmount && $addFundsToken && $pythPrices && $pythPrices[$addFundsToken]) {
			return toBN(
				new BigNumber($addFundsTokenAmount.toString())
					.multipliedBy($pythPrices[$addFundsToken].price)
					.div(`1e${$pythPrices[$addFundsToken].decimals}`)
					.div(`1e${getDecimalOfToken($addFundsToken)}`)
					.toString()
			);
		} else {
			return toBN('0');
		}
	}
);

export const addFundsInsufficientBalance: Readable<AddFundsInsufficientBalanceStatus> = derived(
	[nativeUserBalances, addFundsTokenAmount, addFundsToken, pythPrices],
	([$nativeUserBalances, $addFundsTokenAmount, $addFundsToken, $pythPrices]) => {
		if (
			$nativeUserBalances &&
			$nativeUserBalances[$addFundsToken] &&
			$addFundsTokenAmount &&
			$addFundsTokenAmount.gt(0) &&
			$pythPrices &&
			$pythPrices[$addFundsToken]
		) {
			let isInsufficient = new BigNumber($nativeUserBalances[$addFundsToken].balance).lt(
				new BigNumber($addFundsTokenAmount.toString())
			);

			if ($addFundsToken == 'ETH') {
				let noGasFeeLeft = new BigNumber($nativeUserBalances['ETH'].balance)
					.minus(toBN(MIN_ETH_BALANCE).toString())
					.lt(new BigNumber($addFundsTokenAmount.toString()));

				if (!isInsufficient && noGasFeeLeft)
					return { isInsufficient: noGasFeeLeft, reason: "Can't Deposit All ETH" };
			}
			return {
				isInsufficient,
				reason: isInsufficient ? 'Insufficent Balance' : ''
			};
		}
		return {
			isInsufficient: false,
			reason: ''
		};
	}
);

export const processingFundWithdraw: Writable<boolean> = writable(false);
export const withdrawToken: Writable<TokenSymbol> = writable('sUSD');
export const withdrawFundsTokenAmount: Writable<ethers.BigNumber> = writable(toBN('0'));
export const withdrawFundsAmount: Readable<ethers.BigNumber> = derived(
	[withdrawFundsTokenAmount, withdrawToken, pythPrices],
	([$withdrawFundsTokenAmount, $withdrawToken, $pythPrices]) => {
		if ($withdrawFundsTokenAmount && $withdrawToken && $pythPrices && $pythPrices[$withdrawToken]) {
			return toBN(
				new BigNumber($withdrawFundsTokenAmount.toString())
					.multipliedBy($pythPrices[$withdrawToken].price)
					.div(`1e${$pythPrices[$withdrawToken].decimals}`)
					.div(`1e${getDecimalOfToken($withdrawToken)}`)
					.toString()
			);
		} else {
			return toBN('0');
		}
	}
);
export const withdrawFundsInsufficientBalance: Readable<AddFundsInsufficientBalanceStatus> =
	derived(
		[userBalances, withdrawFundsTokenAmount, withdrawToken],
		([$userBalances, $withdrawFundsTokenAmount, $withdrawToken]) => {
			if (
				$userBalances &&
				$userBalances[$withdrawToken] &&
				$withdrawFundsTokenAmount &&
				$withdrawFundsTokenAmount.gt(0)
			) {
				let isInsufficient = new BigNumber($userBalances[$withdrawToken].balance).lt(
					new BigNumber($withdrawFundsTokenAmount.toString())
				);

				return {
					isInsufficient,
					reason: isInsufficient ? 'Insufficent Balance' : ''
				};
			}
			return {
				isInsufficient: false,
				reason: ''
			};
		}
	);

export const authorityAddressToAdd: Writable<`0x${string}` | undefined> = writable();

export const tokensInWalletUSD: Readable<BigNumber> = derived(
	[userBalancesEnriched, tokensAllowedForDeposit],
	([$userBalancesEnriched, $tokensAllowedForDeposit]) => {
		let total = new BigNumber('0');
		if (
			Object.keys($userBalancesEnriched).length !== 0 &&
			$tokensAllowedForDeposit.reduce((accumulator, token) => {
				return accumulator && $userBalancesEnriched[token] !== undefined;
			}, true)
		) {
			$tokensAllowedForDeposit.forEach((token) => {
				total = total.plus(
					BigNumber($userBalancesEnriched[token].balanceUSD).multipliedBy(
						`1e${18 - parseInt($userBalancesEnriched[token].decimals)}`
					)
				);
			});
		}

		return total;
	}
);
